import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminApiService from '../services/api';
import './ApplicationDetail.css';

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [expectedReturn, setExpectedReturn] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [savingDraftNotes, setSavingDraftNotes] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [draftReturnFile, setDraftReturnFile] = useState(null);
  const [finalReturnFile, setFinalReturnFile] = useState(null);
  const [uploadingDraft, setUploadingDraft] = useState(false);
  const [uploadingFinal, setUploadingFinal] = useState(false);
  const [uploadedReturns, setUploadedReturns] = useState({});
  const [documentCategory, setDocumentCategory] = useState('w2Forms');
  const [additionalIncomeSourceType, setAdditionalIncomeSourceType] = useState('Rental Income');
  const [additionalIncomeAmount, setAdditionalIncomeAmount] = useState('');
  const [additionalIncomeDescription, setAdditionalIncomeDescription] = useState('');
  const [dependentUploadMode, setDependentUploadMode] = useState('existing'); // 'existing' | 'new'
  const [dependentIdForUpload, setDependentIdForUpload] = useState('');
  const [newDependentName, setNewDependentName] = useState('');
  const [newDependentDob, setNewDependentDob] = useState('');
  const [newDependentSsn, setNewDependentSsn] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [pendingFinalFile, setPendingFinalFile] = useState(null);
  const [showFinalUploadWarning, setShowFinalUploadWarning] = useState(false);
  const finalReturnInputRef = useRef(null);

  useEffect(() => {
    fetchApplicationDetails();
  }, [id]);

  useEffect(() => {
    if (application && application.id) {
      loadUploadedReturns();
    }
  }, [application]);

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminApiService.getTaxFormDetails(id);
      if (response.success) {
        setApplication(response.data);
        setExpectedReturn(response.data.expectedReturn || '');
        setPaymentAmount(response.data.paymentAmount || '');
        setAdminNotes(response.data.adminNotes || '');
        setDraftNotes(response.data.draftNotes || '');
      } else {
        setError('Failed to fetch application details');
      }
    } catch (err) {
      console.error('Error fetching application details:', err);
      setError('Error loading application details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    try {
      setUpdating(true);
      const response = await AdminApiService.updateTaxFormStatus(
        id,
        status,
        parseFloat(expectedReturn) || 0,
        parseFloat(paymentAmount) || 0,
        adminNotes,
        draftNotes
      );
      
      if (response.success) {
        setApplication(prev => ({
          ...prev,
          status: response.data.status,
          expectedReturn: response.data.expectedReturn,
          paymentAmount: response.data.paymentAmount,
          adminNotes: response.data.adminNotes,
          draftNotes: response.data.draftNotes ?? prev.draftNotes,
          draftNotesUpdatedAt: response.data.draftNotesUpdatedAt
        }));
        setShowStatusModal(false);
        setSelectedStatus(''); // Reset selected status
        // Update select value to new status
        const select = document.getElementById('status-select');
        if (select) {
          select.value = response.data.status || '';
        }
        alert(`Application status updated to ${formatStatusName(status)} successfully!`);
      } else {
        alert('Failed to update application status');
        // Reset select on error
        const select = document.getElementById('status-select');
        if (select && application) {
          select.value = application.status || '';
        }
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error updating application status');
      // Reset select on error
      const select = document.getElementById('status-select');
      if (select && application) {
        select.value = application.status || '';
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusSelectChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus && newStatus !== application.status) {
      setSelectedStatus(newStatus);
      setShowStatusModal(true);
      // Reset select to current status immediately (will update after confirmation)
      setTimeout(() => {
        e.target.value = application.status || '';
      }, 0);
    }
  };

  const handleSaveAdminNotes = async () => {
    try {
      setSavingNotes(true);
      const response = await AdminApiService.updateTaxFormStatus(
        id,
        application.status,
        parseFloat(expectedReturn) || 0,
        parseFloat(paymentAmount) || 0,
        adminNotes,
        draftNotes
      );
      
      if (response.success) {
        setApplication(prev => ({
          ...prev,
          adminNotes: response.data.adminNotes,
          adminNotesUpdatedAt: response.data.adminNotesUpdatedAt,
          draftNotes: response.data.draftNotes ?? prev.draftNotes,
          draftNotesUpdatedAt: response.data.draftNotesUpdatedAt
        }));
        alert('Admin notes saved successfully!');
      } else {
        alert('Failed to save admin notes');
      }
    } catch (err) {
      console.error('Error saving admin notes:', err);
      alert('Error saving admin notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveDraftNotes = async () => {
    try {
      setSavingDraftNotes(true);
      const response = await AdminApiService.updateTaxFormStatus(
        id,
        application.status,
        parseFloat(expectedReturn) || 0,
        parseFloat(paymentAmount) || 0,
        adminNotes,
        draftNotes
      );
      if (response.success) {
        setApplication(prev => ({
          ...prev,
          draftNotes: response.data.draftNotes ?? draftNotes,
          draftNotesUpdatedAt: response.data.draftNotesUpdatedAt
        }));
        alert('Message to user saved. It will be shown when the user views their draft.');
      } else {
        alert('Failed to save message');
      }
    } catch (err) {
      console.error('Error saving draft notes:', err);
      alert('Error saving message to user');
    } finally {
      setSavingDraftNotes(false);
    }
  };

  const handleDeleteApplication = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this application?\n\nApplication ID: ${id}\nUser: ${application?.userName || 'Unknown'}\n\nThis action cannot be undone and will permanently remove the application from the database.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setUpdating(true);
      const response = await AdminApiService.deleteTaxForm(id);
      
      if (response.success) {
        alert('Application deleted successfully!');
        // Navigate back to applications list
        navigate('/admin/applications');
      } else {
        alert('Failed to delete application. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Error deleting application. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  /** Format SSN for admin display: 9 digits -> XXX-XX-XXXX */
  const formatSsnForDisplay = (ssn) => {
    if (!ssn) return '';
    const d = String(ssn).replace(/\D/g, '').slice(0, 9);
    return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d || '';
  };

  /** Format DOB for display: YYYY-MM-DD -> MM/DD/YYYY; 8-digit number (MMDDYYYY) -> MM/DD/YYYY */
  const formatDobForDisplay = (dob) => {
    if (!dob) return '';
    const s = String(dob).trim();
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
    if (/^\d{8}$/.test(s)) {
      const mm = s.slice(0, 2), dd = s.slice(2, 4), yyyy = s.slice(4, 8);
      return `${mm}/${dd}/${yyyy}`;
    }
    return s;
  };

  const openDocument = async (doc) => {
    try {
      console.log(`🔓 Opening document: ${doc.name}`);
      
      // Create a blob URL from the file response
      const response = await AdminApiService.getSecureFileUrl(doc.gcsPath);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      console.log(`🔗 Document blob URL created`);
      window.open(blobUrl, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000); // Clean up after 1 minute
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Error opening document. Please try again.');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      console.log(`📥 Downloading document: ${doc.name}`);
      
      // Use the new download endpoint that handles decryption
      const response = await AdminApiService.downloadFile(doc.gcsPath);
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create a blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.name || 'document';
      link.style.display = 'none';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      console.log(`✅ Document downloaded successfully: ${doc.name}`);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Error downloading document: ${error.message}`);
    }
  };

  const downloadAllDocuments = async () => {
    if (!application.documents || application.documents.length === 0) {
      alert('No documents to download');
      return;
    }

    try {
      console.log(`📦 Starting download of all ${application.documents.length} documents as ZIP...`);
      alert(`Starting download of all ${application.documents.length} documents as ZIP...`);
      
      // Use the new download all endpoint that creates a ZIP file
      const response = await AdminApiService.downloadAllFiles(application.id);
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create a blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `application-${application.id}-documents.zip`;
      link.style.display = 'none';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      console.log(`✅ ZIP archive downloaded successfully`);
    } catch (error) {
      console.error('Error downloading all documents:', error);
      alert(`Error downloading all documents: ${error.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    
    // Handle Firestore Timestamp objects
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle Firestore Timestamp serialized objects
    else if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Handle regular Date objects or date strings
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    // Handle timestamp in milliseconds
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    else {
      console.warn('Unknown timestamp format:', timestamp);
      return 'Invalid Date';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', timestamp);
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatStatusName = (status) => {
    if (!status) return 'UNKNOWN';
    // Replace all underscores with spaces and capitalize each word
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const formatImmigrationStatus = (value) => {
    if (!value) return '—';
    const labels = { us_citizen: 'U.S. Citizen', permanent_resident: 'Permanent Resident (Green Card)', resident_alien: 'Resident Alien', nonresident_alien: 'Nonresident Alien', other: 'Other / Not sure' };
    return labels[value] || value;
  };

  const formatStateCode = (code) => {
    if (!code) return '—';
    const states = { AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming' };
    return states[code] || code;
  };

  const formatHistoryAction = (action) => {
    const actionMap = {
      'application_created': 'Application Created',
      'status_changed': 'Status Changed',
      'notes_updated': 'Admin Notes Updated',
      'user_note_sent': 'User note sent',
      'document_uploaded': 'Document Uploaded',
      'draft_return_uploaded': 'Draft return uploaded',
      'final_return_uploaded': 'Final return uploaded',
      'expected_return_updated': 'Expected Return Updated',
      'payment_amount_updated': 'Payment Amount Updated'
    };
    return actionMap[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getHistoryDescription = (historyEntry) => {
    const { action, details } = historyEntry;
    
    switch (action) {
      case 'application_created':
        return `Application submitted with ${details.documentCount || 0} document(s) for tax year ${details.taxYear || 'N/A'}`;
      case 'status_changed':
        return `Status changed from "${formatStatusName(details.oldStatus)}" to "${formatStatusName(details.newStatus)}"`;
      case 'notes_updated':
        return details.hasNotes ? `Admin notes updated (${details.notesLength} characters)` : 'Admin notes cleared';
      case 'user_note_sent':
        return details.noteText ? `"${details.noteText.length > 120 ? details.noteText.slice(0, 120) + '...' : details.noteText}"` : 'User sent a note';
      case 'document_uploaded':
        return `Document "${details.documentName}" uploaded in category "${details.documentCategory}" (${(details.documentSize / 1024).toFixed(2)} KB)`;
      case 'draft_return_uploaded':
        return details.documentName ? `Draft return "${details.documentName}" uploaded (${details.sizeInKb || '—'} KB)` : 'Draft return uploaded';
      case 'final_return_uploaded':
        return details.documentName ? `Final return "${details.documentName}" uploaded (${details.sizeInKb || '—'} KB)` : 'Final return uploaded';
      case 'expected_return_updated':
        return `Expected return updated from $${details.oldValue.toFixed(2)} to $${details.newValue.toFixed(2)}`;
      case 'payment_amount_updated':
        return `Payment amount updated from $${details.oldValue.toFixed(2)} to $${details.newValue.toFixed(2)}`;
      default:
        return 'Action performed';
    }
  };

  // When image/doc is related to additional income or dependent, show descriptive label (e.g. "Additional income – Freelance", "Dependent – John")
  const getDocumentCategoryLabel = (doc) => {
    if (!doc) return '';
    const cat = doc.category || '';
    if (cat === 'additional_income' || cat === 'additionalIncomeGeneral') {
      if (doc.incomeSourceType) return `Additional income – ${doc.incomeSourceType}`;
      const sources = application?.additionalIncomeSources || [];
      const source = sources.find(inc =>
        (inc.documents || []).some(d => (d.id && d.id === doc.id) || (d.gcsPath && d.gcsPath === doc.gcsPath))
      );
      if (source && source.source) return `Additional income – ${source.source}`;
      return cat === 'additionalIncomeGeneral' ? 'Additional income (general)' : 'Additional income';
    }
    if (cat === 'dependent') {
      const depId = doc.dependentId != null ? String(doc.dependentId) : '';
      const dep = (application?.dependents || []).find(d => (d.id != null && String(d.id) === depId) || (depId && d.id === depId));
      const name = dep?.name || doc.dependentName || '';
      return name ? `Dependent – ${name}` : 'Dependent';
    }
    return cat;
  };

  const getStatusColor = (status) => {
    const colors = {
      // New states
      new_application_submitted: '#3498db', // Blue - new application
      processing: '#f39c12', // Orange - admin reviewing
      awaiting_for_documents: '#e67e22', // Dark orange - waiting for docs
      new_documents_submitted: '#3498db', // Blue - new docs uploaded
      draft_uploaded: '#9b59b6', // Purple - draft ready
      draft_rejected: '#e74c3c', // Red - draft rejected
      payment_completed: '#27ae60', // Green - payment done
      close_application: '#2ecc71', // Green - application closed
      // Legacy states (for backward compatibility)
      submitted: '#3498db',
      under_review: '#f39c12',
      approved: '#27ae60',
      rejected: '#e74c3c',
      completed: '#2ecc71'
    };
    return colors[status] || '#95a5a6';
  };

  const loadUploadedReturns = async () => {
    try {
      const response = await AdminApiService.getReturns(application.id);
      if (response.success) {
        setUploadedReturns(response.data);
      }
    } catch (error) {
      console.error('Error loading uploaded returns:', error);
    }
  };

  const downloadReturn = async (returnType) => {
    try {
      const response = await AdminApiService.downloadReturn(application.id, returnType);
      if (response.success) {
        // Open the download URL in a new tab
        window.open(response.downloadUrl, '_blank');
      } else {
        throw new Error(response.error || 'Download failed');
      }
    } catch (error) {
      console.error(`Error downloading ${returnType} return:`, error);
      alert(`Error downloading ${returnType} return: ${error.message}`);
    }
  };

  const viewReturn = async (returnType) => {
    try {
      // Use the new decryption endpoint for viewing
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const viewUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/admin/returns/${application.id}/${returnType}/view?token=${token}&t=${timestamp}`;
      console.log(`🔓 Opening decrypted view URL: ${viewUrl}`);
      console.log(`📁 Current uploaded returns state:`, uploadedReturns);
      
      // Open the decrypted file in a new tab
      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error(`Error viewing ${returnType} return:`, error);
      alert(`Error viewing ${returnType} return: ${error.message}`);
    }
  };

  const handleDraftReturnUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if application is loaded
    if (!application || !application.id) {
      alert('Application not loaded yet. Please wait and try again.');
      return;
    }

    // File validation
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only PDF, DOC, and DOCX files are allowed.');
      return;
    }

    if (file.size > maxSize) {
      alert('File size too large. Maximum size is 10MB.');
      return;
    }

    setDraftReturnFile(file);
    setUploadingDraft(true);

    try {
      console.log('Uploading draft return:', file.name);
      console.log('Application ID:', application.id);
      
      const response = await AdminApiService.uploadReturn(application.id, 'draft', file);
      
      if (response.success) {
        alert('Draft return uploaded successfully!');
        // Refresh application data to show the uploaded file
        await fetchApplicationDetails();
        // Also refresh uploaded returns specifically
        await loadUploadedReturns();
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading draft return:', error);
      alert(`Error uploading draft return: ${error.message}`);
    } finally {
      setUploadingDraft(false);
      setDraftReturnFile(null);
    }
  };

  const doFinalReturnUpload = async (file) => {
    if (!application?.id || !file) return;
    setFinalReturnFile(file);
    setUploadingFinal(true);
    try {
      const response = await AdminApiService.uploadReturn(application.id, 'final', file);
      if (response.success) {
        alert('Final return uploaded successfully!');
        await fetchApplicationDetails();
        await loadUploadedReturns();
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading final return:', error);
      alert(`Error uploading final return: ${error.message}`);
    } finally {
      setUploadingFinal(false);
      setFinalReturnFile(null);
      setPendingFinalFile(null);
      if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
    }
  };

  const handleFinalReturnUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!application || !application.id) {
      alert('Application not loaded yet. Please wait and try again.');
      if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
      return;
    }

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only PDF, DOC, and DOCX files are allowed.');
      if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
      return;
    }

    if (file.size > maxSize) {
      alert('File size too large. Maximum size is 10MB.');
      if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
      return;
    }

    // Warn if user has not made payment
    if (application.status !== 'payment_completed') {
      setPendingFinalFile(file);
      setShowFinalUploadWarning(true);
      if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
      return;
    }

    await doFinalReturnUpload(file);
    if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
  };

  const handleFinalUploadWarningCancel = () => {
    setShowFinalUploadWarning(false);
    setPendingFinalFile(null);
    if (finalReturnInputRef.current) finalReturnInputRef.current.value = '';
  };

  const handleFinalUploadWarningProceed = async () => {
    const file = pendingFinalFile;
    setShowFinalUploadWarning(false);
    setPendingFinalFile(null);
    if (file) await doFinalReturnUpload(file);
  };

  const DOCUMENT_CATEGORY_OPTIONS = [
    { value: 'w2Forms', label: 'W-2 Forms' },
    { value: 'medical', label: 'Medical Documents' },
    { value: 'education', label: 'Education Documents' },
    { value: 'dependentChildren', label: 'Dependent Children (general)' },
    { value: 'dependent', label: 'Dependent (select or add below)' },
    { value: 'homeownerDeduction', label: 'Homeowner Deduction' },
    { value: 'personalId', label: 'Personal ID' },
    { value: 'previousYearTax', label: 'Previous Year Tax' },
    { value: 'previousYearTaxDocuments', label: 'Previous Year Tax Documents' },
    { value: 'additionalIncomeGeneral', label: 'Additional Income (General)' },
    { value: 'additional_income', label: 'Additional Income (select type below)' },
    { value: 'other', label: 'Other' }
  ];

  const ADDITIONAL_INCOME_SOURCE_OPTIONS = [
    'Investment Income (Stocks, Bonds)',
    'Rental Income',
    'Freelance/Self-Employment',
    'Interest Income (Savings, CDs)',
    'Dividend Income',
    'Capital Gains (Property Sale)',
    'Business Income',
    'Royalty Income',
    'Pension/Annuity Income',
    'Unemployment Benefits',
    'Social Security Benefits',
    'Other'
  ];

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!documentFile || !application?.id) {
      alert('Please select a category and a file.');
      return;
    }
    if (documentCategory === 'additional_income' && !additionalIncomeSourceType) {
      alert('Please select an additional income source type.');
      return;
    }
    if (documentCategory === 'dependent') {
      if (dependentUploadMode === 'existing' && !dependentIdForUpload) {
        alert('Please select a dependent or choose "Add new dependent".');
        return;
      }
      if (dependentUploadMode === 'new' && !newDependentName.trim()) {
        alert('Please enter the dependent\'s name for a new dependent.');
        return;
      }
    }
    setUploadingDocument(true);
    try {
      const incomeSourceType = documentCategory === 'additional_income' ? additionalIncomeSourceType : undefined;
      const incomeAmount = documentCategory === 'additional_income' ? additionalIncomeAmount : undefined;
      const incomeDescription = documentCategory === 'additional_income' ? additionalIncomeDescription : undefined;
      const dependentId = documentCategory === 'dependent' && dependentUploadMode === 'existing' ? dependentIdForUpload : undefined;
      const dependentName = documentCategory === 'dependent' && dependentUploadMode === 'new' ? newDependentName.trim() : undefined;
      const dependentDob = documentCategory === 'dependent' && dependentUploadMode === 'new' ? newDependentDob.trim() : undefined;
      const dependentSsn = documentCategory === 'dependent' && dependentUploadMode === 'new' ? newDependentSsn.trim() : undefined;
      const response = await AdminApiService.uploadDocument(application.id, documentCategory, documentFile, {
        incomeSourceType, incomeAmount, incomeDescription,
        dependentId, dependentName, dependentDob, dependentSsn
      });
      if (response.success) {
        alert('Document uploaded successfully.');
        setDocumentFile(null);
        if (documentCategory === 'dependent' && dependentUploadMode === 'new') {
          setNewDependentName('');
          setNewDependentDob('');
          setNewDependentSsn('');
        }
        await fetchApplicationDetails();
      } else throw new Error(response.error || 'Upload failed');
    } catch (err) {
      console.error('Error uploading document:', err);
      alert(err.message || 'Failed to upload document.');
    } finally {
      setUploadingDocument(false);
    }
  };

  if (loading) {
    return (
      <div className="application-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading application details...</p>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="application-detail-error">
        <h2>Error</h2>
        <p>{error || 'Application not found'}</p>
        <button onClick={() => navigate('/admin/applications')}>
          Back to Applications
        </button>
      </div>
    );
  }

  return (
    <div className="application-detail">
      {/* Header */}
      <div className="application-detail-header">
        <button 
          className="back-button"
          onClick={() => navigate('/admin/applications')}
        >
          ← Back to Applications
        </button>
        <div className="header-info">
          <h1>Application Details</h1>
          <div 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(application.status) }}
          >
            {formatStatusName(application.status)}
          </div>
        </div>
      </div>

      {/* Payment completed note – visible when user has made payment */}
      {application.status === 'payment_completed' && (
        <div className="info-card payment-completed-banner">
          <div className="payment-completed-content">
            <span className="payment-completed-icon">✓</span>
            <div>
              <strong>Payment completed.</strong> The user has made the payment for this application.
            </div>
          </div>
        </div>
      )}

      {/* User Information - complete profile */}
      <div className="info-card">
        <h3>User Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>First Name:</label>
            <span>{application.user?.firstName ?? application.userName?.split(' ')[0] ?? '—'}</span>
          </div>
          <div className="info-item">
            <label>Last Name:</label>
            <span>{application.user?.lastName ?? application.userName?.split(' ').slice(1).join(' ') ?? '—'}</span>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <span>{application.user?.email || application.userEmail || '—'}</span>
          </div>
          <div className="info-item">
            <label>Phone:</label>
            <span>{application.user?.phone || '—'}</span>
          </div>
          <div className="info-item">
            <label>User ID:</label>
            <span>{application.userId || application.user?.id || '—'}</span>
          </div>
          <div className="info-item">
            <label>Date of Birth:</label>
            <span>{application.user?.dateOfBirth || '—'}</span>
          </div>
          <div className="info-item">
            <label>Street Address:</label>
            <span>{application.user?.address || '—'}</span>
          </div>
          <div className="info-item">
            <label>City:</label>
            <span>{application.user?.city || '—'}</span>
          </div>
          <div className="info-item">
            <label>State:</label>
            <span>{application.user?.state || '—'}</span>
          </div>
          <div className="info-item">
            <label>ZIP Code:</label>
            <span>{application.user?.zipCode || '—'}</span>
          </div>
          <div className="info-item">
            <label>Filing Status (profile):</label>
            <span>{application.user?.filingStatus || '—'}</span>
          </div>
          <div className="info-item">
            <label>Occupation:</label>
            <span>{application.user?.occupation || '—'}</span>
          </div>
          <div className="info-item">
            <label>Employer:</label>
            <span>{application.user?.employer || '—'}</span>
          </div>
        </div>
      </div>

      {/* Application Details */}
      <div className="info-card">
        <h3>Application Details</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Application ID:</label>
            <span>{application.id}</span>
          </div>
          <div className="info-item">
            <label>Tax Year:</label>
            <span>{application.taxYear || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Form Type:</label>
            <span>{application.formType || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Filing Status:</label>
            <span>{application.filingStatus || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>SSN:</label>
            <span>{application.socialSecurityNumber || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Submitted:</label>
            <span>{formatDate(application.submittedAt)}</span>
          </div>
        </div>
      </div>

      {/* Personal & Residency (Step 4) */}
      <div className="info-card">
        <h3>Personal & Residency (Step 4)</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Immigration Status:</label>
            <span>{formatImmigrationStatus(application.immigrationStatus)}</span>
          </div>
          <div className="info-item">
            <label>State of Residence:</label>
            <span>{formatStateCode(application.stateOfResidence)}</span>
          </div>
          {application.bankAccount && (application.bankAccount.routingNumber || application.bankAccount.accountNumber) ? (
            <>
              <div className="info-item">
                <label>Bank Name:</label>
                <span>{application.bankAccount.bankName || '—'}</span>
              </div>
              <div className="info-item">
                <label>Account Type:</label>
                <span>{application.bankAccount.accountType === 'checking' ? 'Checking' : application.bankAccount.accountType === 'savings' ? 'Savings' : application.bankAccount.accountType || '—'}</span>
              </div>
              <div className="info-item">
                <label>Account (last 4):</label>
                <span>****{((application.bankAccount.accountNumber || '').replace(/\D/g, '')).slice(-4) || '—'}</span>
              </div>
              <div className="info-item">
                <label>Account Holder:</label>
                <span>{application.bankAccount.accountHolderName || '—'}</span>
              </div>
            </>
          ) : (
            <div className="info-item">
              <label>Bank Account:</label>
              <span>Not provided</span>
            </div>
          )}
        </div>
        {(application.stateWorkStays && application.stateWorkStays.length > 0) && (
          <div className="state-work-stays-section">
            <h4 className="state-work-stays-title">Where applicant worked (by state)</h4>
            <ul className="state-work-stays-list">
              {application.stateWorkStays.map((entry, idx) => (
                <li key={entry.id || idx} className="state-work-stay-item">
                  <span className="state-work-stay-state">{formatStateCode(entry.state)}</span>
                  <span className="state-work-stay-period">
                    {MONTH_NAMES[(entry.fromMonth || 1) - 1]} {entry.fromYear || '—'} – {MONTH_NAMES[(entry.toMonth || 12) - 1]} {entry.toYear || '—'}
                  </span>
                  {(entry.documents && entry.documents.length > 0) && (
                    <span className="state-work-stay-docs">({entry.documents.length} doc{entry.documents.length !== 1 ? 's' : ''})</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="info-card">
        <div className="documents-header">
          <h3>Documents ({application.documents?.length || 0} files)</h3>
          {application.documents && application.documents.length > 0 && (
            <button 
              className="download-all-button"
              onClick={downloadAllDocuments}
            >
              📥 Download All
            </button>
          )}
        </div>
        <div className="admin-upload-document-section">
          <h4 className="admin-upload-document-title">Upload document (offline / from you)</h4>
          <p className="admin-upload-document-hint">Select the form field category and a file to add it to this application.</p>
          <form onSubmit={handleUploadDocument} className="admin-upload-document-form">
            <div className="form-group">
              <label htmlFor="documentCategory">Category (form field)</label>
              <select
                id="documentCategory"
                value={documentCategory}
                onChange={(e) => setDocumentCategory(e.target.value)}
                className="admin-upload-select"
              >
                {DOCUMENT_CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {documentCategory === 'additional_income' && (
              <>
                <div className="form-group">
                  <label htmlFor="additionalIncomeSourceType">Income source</label>
                  <select
                    id="additionalIncomeSourceType"
                    value={additionalIncomeSourceType}
                    onChange={(e) => setAdditionalIncomeSourceType(e.target.value)}
                    className="admin-upload-select"
                  >
                    {ADDITIONAL_INCOME_SOURCE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="additionalIncomeAmount">Amount ($)</label>
                  <input
                    id="additionalIncomeAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={additionalIncomeAmount}
                    onChange={(e) => setAdditionalIncomeAmount(e.target.value)}
                    className="admin-upload-input"
                  />
                </div>
                <div className="form-group admin-upload-description-group">
                  <label htmlFor="additionalIncomeDescription">Description (optional)</label>
                  <input
                    id="additionalIncomeDescription"
                    type="text"
                    placeholder="e.g. Freelance work for Q1 2026"
                    value={additionalIncomeDescription}
                    onChange={(e) => setAdditionalIncomeDescription(e.target.value)}
                    className="admin-upload-input"
                  />
                </div>
              </>
            )}
            {documentCategory === 'dependent' && (
              <>
                <div className="form-group">
                  <label>Dependent</label>
                  <select
                    value={dependentUploadMode}
                    onChange={(e) => { setDependentUploadMode(e.target.value); setDependentIdForUpload(''); }}
                    className="admin-upload-select"
                  >
                    <option value="existing">Select existing dependent</option>
                    <option value="new">Add new dependent</option>
                  </select>
                </div>
                {dependentUploadMode === 'existing' && (
                  <div className="form-group">
                    <label htmlFor="dependentIdForUpload">Select dependent</label>
                    <select
                      id="dependentIdForUpload"
                      value={dependentIdForUpload}
                      onChange={(e) => setDependentIdForUpload(e.target.value)}
                      className="admin-upload-select"
                    >
                      <option value="">— Select —</option>
                      {(application.dependents || []).map((dep) => (
                        <option key={dep.id || dep.name} value={dep.id || ''}>
                          {dep.name || 'Unnamed'} {dep.dob ? `(${formatDobForDisplay(dep.dob)})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {dependentUploadMode === 'new' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="newDependentName">Name *</label>
                      <input
                        id="newDependentName"
                        type="text"
                        placeholder="Dependent full name"
                        value={newDependentName}
                        onChange={(e) => setNewDependentName(e.target.value)}
                        className="admin-upload-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="newDependentDob">Date of birth</label>
                      <input
                        id="newDependentDob"
                        type="date"
                        value={newDependentDob}
                        onChange={(e) => setNewDependentDob(e.target.value)}
                        className="admin-upload-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="newDependentSsn">SSN (optional)</label>
                      <input
                        id="newDependentSsn"
                        type="text"
                        placeholder="XXX-XX-XXXX"
                        value={newDependentSsn}
                        onChange={(e) => setNewDependentSsn(e.target.value)}
                        className="admin-upload-input"
                        maxLength={11}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div className="form-group">
              <label htmlFor="documentFile">File</label>
              <input
                id="documentFile"
                type="file"
                accept=".pdf,.doc,.docx,image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                className="admin-upload-file-input"
              />
              {documentFile && <span className="admin-upload-file-name">{documentFile.name}</span>}
            </div>
            <button
              type="submit"
              className="admin-upload-doc-button"
              disabled={!documentFile || uploadingDocument}
            >
              {uploadingDocument ? 'Uploading...' : '📤 Upload document'}
            </button>
          </form>
        </div>
        {application.documents && application.documents.length > 0 ? (
          <div className="documents-grid">
            {application.documents.map((doc, index) => (
              <div key={index} className="document-item">
                <div className="document-preview" onClick={() => openDocument(doc)}>
                  <div className="document-icon">
                    {doc.type?.startsWith('image/') ? '🖼️' : '📄'}
                  </div>
                  <div className="document-info">
                    <div className="document-name">{doc.name}</div>
                    <div className="document-category">{getDocumentCategoryLabel(doc)}</div>
                    <div className="document-date">
                      {formatDate(doc.uploadedAt)}
                    </div>
                  </div>
                </div>
                <div className="document-actions">
                  <button 
                    className="action-button open-button"
                    onClick={() => openDocument(doc)}
                  >
                    Open
                  </button>
                  <button 
                    className="action-button download-button"
                    onClick={() => downloadDocument(doc)}
                  >
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>No documents uploaded</p>
        )}
      </div>

      {/* Dependents */}
      <div className="info-card">
        <h3>Dependents ({application.dependents?.length || 0})</h3>
        <p className="info-card-subtitle">Each dependent can have their own documents. Use the upload section above with category &quot;Dependent (select or add below)&quot; to add documents.</p>
        {application.dependents && application.dependents.length > 0 ? (
          <div className="dependents-table additional-income-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Relationship</th>
                  <th>DOB</th>
                  <th>SSN</th>
                  <th>Documents</th>
                </tr>
              </thead>
              <tbody>
                {application.dependents.map((dep, index) => (
                  <tr key={dep.id || index}>
                    <td className="row-number">{index + 1}</td>
                    <td>{dep.name || '—'}</td>
                    <td>{dep.age ?? '—'}</td>
                    <td>{dep.relationship || '—'}</td>
                    <td>{formatDobForDisplay(dep.dob) || '—'}</td>
                    <td>{formatSsnForDisplay(dep.ssn) || '—'}</td>
                    <td>
                      <div className="document-count">
                        {dep.documents && dep.documents.length > 0 ? (
                          <>
                            <span className="document-badge">{dep.documents.length} file{dep.documents.length !== 1 ? 's' : ''}</span>
                            <div className="document-list">
                              {dep.documents.map((doc, docIdx) => (
                                <div key={doc.id || docIdx} className="document-item-small">
                                  <span className="document-icon">📄</span>
                                  <span className="document-name">{doc.name}</span>
                                  {doc.publicUrl ? (
                                    <a href={doc.publicUrl} target="_blank" rel="noopener noreferrer" className="document-view-link">View</a>
                                  ) : (
                                    <button type="button" className="document-view-link" onClick={() => openDocument(doc)}>View</button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <span className="no-documents">No documents</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No dependents listed</p>
        )}
      </div>

      {/* Additional Income Sources */}
      <div className="info-card">
        <h3>Additional Income Sources ({application.additionalIncomeSources?.length || 0})</h3>
        <p className="info-card-subtitle">Each row is a separate income source; documents are tied to that source.</p>
        {application.additionalIncomeSources && application.additionalIncomeSources.length > 0 ? (
          <div className="additional-income-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Source</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Documents</th>
                </tr>
              </thead>
              <tbody>
                {application.additionalIncomeSources.map((income, index) => (
                  <tr key={income.id || index}>
                    <td className="row-number">{index + 1}</td>
                    <td><strong>{income.source || 'N/A'}</strong></td>
                    <td className="amount-cell">${parseFloat(income.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}</td>
                    <td>{income.description || '—'}</td>
                    <td>
                      {income.documents && income.documents.length > 0 ? (
                        <div className="document-count">
                          <span className="document-badge">
                            {income.documents.length} file{income.documents.length !== 1 ? 's' : ''}
                          </span>
                          <div className="document-list">
                            {income.documents.map((doc, docIndex) => (
                              <div key={doc.id || docIndex} className="document-item-small">
                                <span className="document-icon">📄</span>
                                <span className="document-name" title={doc.name || doc.fileName}>{doc.name || doc.fileName || 'Document'}</span>
                                <span className={`document-status ${doc.status || 'completed'}`}>
                                  {doc.status === 'uploading' ? 'Uploading...' :
                                   doc.status === 'completed' ? 'Uploaded' :
                                   doc.status === 'error' ? 'Failed' : 'Uploaded'}
                                </span>
                                {(doc.publicUrl || doc.url) && (
                                  <a
                                    href={doc.publicUrl || doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="document-view-link"
                                  >
                                    View
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <span className="no-documents">No documents</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No additional income sources listed</p>
        )}
      </div>

      {/* Admin Actions */}
      <div className="info-card admin-actions">
        <h3>Admin Actions</h3>
        <div className="admin-form">
          <div className="form-group">
            <label htmlFor="expectedReturn">Expected Return ($):</label>
            <input
              type="number"
              id="expectedReturn"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
              placeholder="0.00"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="paymentAmount">Payment Amount ($):</label>
            <input
              type="number"
              id="paymentAmount"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="adminNotes">Tax Consultant Notes</label>
            <p className="field-hint">Internal notes for the tax consultant team (visible to all admins). Not shown to the taxpayer.</p>
            <textarea
              id="adminNotes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this application for your team..."
              rows="5"
              disabled={savingNotes}
            />
            <button
              type="button"
              className="action-button save-notes-button"
              onClick={handleSaveAdminNotes}
              disabled={savingNotes || !application}
            >
              {savingNotes ? '💾 Saving...' : '💾 Save Notes'}
            </button>
            {application?.adminNotesUpdatedAt && (
              <div className="notes-timestamp">
                Last updated: {formatDate(application.adminNotesUpdatedAt)}
              </div>
            )}
          </div>
          
          {/* File Upload Fields */}
          <div className="form-group">
            <label htmlFor="draftReturn">Draft Return Upload:</label>
            <div className="file-upload-container">
              <input
                type="file"
                id="draftReturn"
                accept=".pdf,.doc,.docx"
                onChange={handleDraftReturnUpload}
                disabled={uploadingDraft}
                style={{ display: 'none' }}
              />
              <label htmlFor="draftReturn" className="file-upload-button">
                {uploadingDraft ? (
                  <span>📤 Uploading...</span>
                ) : (
                  <span>📄 {draftReturnFile ? draftReturnFile.name : 'Choose Draft Return File'}</span>
                )}
              </label>
              {draftReturnFile && (
                <button
                  type="button"
                  className="file-remove-button"
                  onClick={() => setDraftReturnFile(null)}
                  disabled={uploadingDraft}
                >
                  ✕
                </button>
              )}
            </div>
            {uploadedReturns.draftReturn && (
              <div className="uploaded-file-info">
                <div className="file-info">
                  <span className="file-name">📄 {uploadedReturns.draftReturn.originalName}</span>
                  <span className="file-size">({(uploadedReturns.draftReturn.size / 1024 / 1024).toFixed(2)} MB)</span>
                  <span className="file-date">{formatDate(uploadedReturns.draftReturn.uploadedAt)}</span>
                </div>
                <div className="file-actions">
                  <button
                    className="view-button"
                    onClick={() => viewReturn('draft')}
                    title="View file in new tab"
                  >
                    👁️ View
                  </button>
                  <button
                    className="download-button"
                    onClick={() => downloadReturn('draft')}
                    title="Download file"
                  >
                    📥 Download
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Message to user (sent with draft) */}
          <div className="form-group">
            <label htmlFor="draftNotes">Message to user (sent with draft):</label>
            <p className="form-group-hint">This message is shown to the user when they view their draft document.</p>
            <textarea
              id="draftNotes"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              placeholder="e.g. Please review the draft and let us know if you have any questions..."
              rows="4"
              disabled={!application}
              className="admin-notes-textarea"
            />
            <button
              type="button"
              className="save-notes-button"
              onClick={handleSaveDraftNotes}
              disabled={savingDraftNotes || !application}
            >
              {savingDraftNotes ? 'Saving...' : '💾 Save message'}
            </button>
            {application?.draftNotesUpdatedAt && (
              <div className="notes-timestamp">
                Last updated: {formatDate(application.draftNotesUpdatedAt)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="finalReturn">Final Return Upload:</label>
            <div className="file-upload-container">
              <input
                ref={finalReturnInputRef}
                type="file"
                id="finalReturn"
                accept=".pdf,.doc,.docx"
                onChange={handleFinalReturnUpload}
                disabled={uploadingFinal}
                style={{ display: 'none' }}
              />
              <label htmlFor="finalReturn" className="file-upload-button">
                {uploadingFinal ? (
                  <span>📤 Uploading...</span>
                ) : (
                  <span>📄 {finalReturnFile ? finalReturnFile.name : 'Choose Final Return File'}</span>
                )}
              </label>
              {finalReturnFile && (
                <button
                  type="button"
                  className="file-remove-button"
                  onClick={() => setFinalReturnFile(null)}
                  disabled={uploadingFinal}
                >
                  ✕
                </button>
              )}
            </div>
            {uploadedReturns.finalReturn && (
              <div className="uploaded-file-info">
                <div className="file-info">
                  <span className="file-name">📄 {uploadedReturns.finalReturn.originalName}</span>
                  <span className="file-size">({(uploadedReturns.finalReturn.size / 1024 / 1024).toFixed(2)} MB)</span>
                  <span className="file-date">{formatDate(uploadedReturns.finalReturn.uploadedAt)}</span>
                </div>
                <div className="file-actions">
                  <button
                    className="view-button"
                    onClick={() => viewReturn('final')}
                    title="View file in new tab"
                  >
                    👁️ View
                  </button>
                  <button
                    className="download-button"
                    onClick={() => downloadReturn('final')}
                    title="Download file"
                  >
                    📥 Download
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="action-buttons">
            <div className="status-update-container">
              <label htmlFor="status-select" className="status-label">Update Status:</label>
              <select
                id="status-select"
                className="status-select"
                value={application.status || ''}
                onChange={handleStatusSelectChange}
                disabled={updating}
                style={{ 
                  backgroundColor: getStatusColor(application.status),
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  minWidth: '200px'
                }}
              >
                <option value={application.status || ''} disabled>
                  {formatStatusName(application.status) || 'Current Status'}
                </option>
                <option value="processing">Processing</option>
                <option value="awaiting_for_documents">Awaiting for Documents</option>
                <option value="draft_uploaded">Draft Uploaded</option>
                <option value="close_application">Close Application</option>
                <option value="rejected">Reject</option>
              </select>
            </div>
            <button 
              className="action-button delete-button"
              onClick={handleDeleteApplication}
              disabled={updating}
            >
              {updating ? 'Deleting...' : 'Delete Application'}
            </button>
          </div>
        </div>
      </div>

      {/* Notes from user (sent via "Send Notes" in the app) */}
      {application?.userNotes && application.userNotes.length > 0 && (
        <div className="info-card">
          <h3>Notes from user</h3>
          <p className="info-card-subtitle">Messages the taxpayer sent using &quot;Send Notes&quot; in the app.</p>
          <div className="user-notes-list">
            {[...application.userNotes].reverse().map((note, index) => (
              <div key={note.createdAt || index} className="user-note-item">
                <p className="user-note-text">{note.text}</p>
                <p className="user-note-meta">
                  {note.userName || 'User'} — {note.createdAt ? formatDate(note.createdAt) : '—'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Application History */}
      <div className="info-card">
        <h3>Application History</h3>
        <p className="history-description">
          Complete audit trail of all changes and activities for this application.
        </p>
        {application.history && application.history.length > 0 ? (
          <div className="history-timeline">
            {application.history.map((entry, index) => (
              <div key={entry.id || index} className="history-entry">
                <div className="history-icon">
                  {entry.performedByType === 'admin' ? '👤' : entry.performedByType === 'user' ? '👤' : '🤖'}
                </div>
                <div className="history-content">
                  <div className="history-header">
                    <span className="history-action">{formatHistoryAction(entry.action)}</span>
                    <span className="history-time">{formatDate(entry.timestamp || entry.createdAt)}</span>
                  </div>
                  <div className="history-description-text">
                    {getHistoryDescription(entry)}
                  </div>
                  <div className="history-performed-by">
                    {entry.performedByType === 'admin' && '👨‍💼 Admin: '}
                    {entry.performedByType === 'user' && '👤 User: '}
                    {entry.performedByType === 'system' && '🤖 System: '}
                    {entry.performedBy || 'Unknown'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-history">
            <p>No history available for this application yet.</p>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Status Update</h3>
            <p>Are you sure you want to set status to "{formatStatusName(selectedStatus)}" for this application?</p>
            <div className="modal-actions">
              <button 
                className="action-button confirm-button"
                onClick={() => handleStatusUpdate(selectedStatus)}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Confirm'}
              </button>
              <button 
                className="action-button cancel-button"
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedStatus('');
                  // Force re-render of select to reset value
                  const select = document.getElementById('status-select');
                  if (select && application) {
                    select.value = application.status || '';
                  }
                }}
                disabled={updating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final return upload warning – user has not made payment */}
      {showFinalUploadWarning && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>User has not made payment</h3>
            <p className="final-upload-warning-text">
              The user has not completed payment for this application. Uploading the final document before payment may not be appropriate.
            </p>
            <p className="final-upload-warning-sub">
              Do you want to cancel and go back, or upload the final document anyway?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="action-button cancel-button"
                onClick={handleFinalUploadWarningCancel}
              >
                Cancel and go back
              </button>
              <button
                type="button"
                className="action-button confirm-button"
                onClick={handleFinalUploadWarningProceed}
              >
                Upload anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetail;
